import { useState, type FormEvent } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { api, ApiError } from '../../lib/api';
import { Button, Field, TextInput } from '../../ui';
import { Wordmark } from '../../app/Shell';

export function LoginPage() {
  const qc = useQueryClient();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const login = useMutation({
    mutationFn: () => api.post('/api/auth/login', { username, password }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['me'] }),
  });
  const submit = (e: FormEvent) => {
    e.preventDefault();
    login.mutate();
  };
  const err = login.error instanceof ApiError ? login.error : null;

  return (
    <div className="grid min-h-dvh lg:grid-cols-[minmax(0,1fr)_minmax(0,1.1fr)]">
      <div className="flex flex-col px-6 py-8 sm:px-12">
        <Wordmark dark />
        <div className="flex flex-1 items-center">
          <form onSubmit={submit} className="mx-auto w-full max-w-sm py-12" noValidate>
            <h1 className="text-[28px] font-semibold leading-tight">Sign in</h1>
            <p className="mt-1 text-muted">Use the username your manager gave you.</p>
            {err && (
              <p className="mt-6 rounded-[8px] bg-danger-soft px-3 py-2.5 text-sm text-danger" role="alert">
                {err.message}
              </p>
            )}
            <div className="mt-6 space-y-4">
              <Field label="Username" htmlFor="username" error={err?.fields.username}>
                <TextInput
                  id="username"
                  autoComplete="username"
                  autoCapitalize="none"
                  spellCheck={false}
                  autoFocus
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  placeholder="e.g. tendai.moyo"
                />
              </Field>
              <Field label="Password" htmlFor="password" error={err?.fields.password}>
                <TextInput
                  id="password"
                  type="password"
                  autoComplete="current-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                />
              </Field>
            </div>
            <Button type="submit" variant="primary" className="mt-6 w-full" busy={login.isPending}>
              Sign in
            </Button>
            <p className="mt-6 text-sm text-muted">Forgot your password? Ask a manager or the owner to reset it.</p>
          </form>
        </div>
      </div>
      <aside className="relative hidden overflow-hidden bg-ledger-800 lg:block" aria-hidden>
        <LedgerArt />
        <div className="absolute inset-x-0 bottom-0 p-12 text-white">
          <p className="max-w-md text-[30px] font-semibold leading-[1.15] tracking-tight">
            Every sale, every kilo, every dollar and ZiG, accounted for.
          </p>
          <p className="mt-3 max-w-md text-white/65">Sales, stock and money for supermarkets, butcheries and furniture shops.</p>
        </div>
      </aside>
    </div>
  );
}

/** Abstract till-roll / ledger lines: the one decorative moment in the app. */
function LedgerArt() {
  const rows = Array.from({ length: 22 }, (_, i) => i);
  return (
    <svg className="absolute inset-0 h-full w-full" preserveAspectRatio="xMidYMid slice" viewBox="0 0 600 800">
      <defs>
        <linearGradient id="fade" x1="0" x2="0" y1="0" y2="1">
          <stop offset="0" stopColor="#12352b" stopOpacity="0" />
          <stop offset="0.75" stopColor="#12352b" stopOpacity="0.95" />
        </linearGradient>
      </defs>
      {rows.map((i) => {
        const y = 40 + i * 34;
        const w = 120 + ((i * 97) % 260);
        return (
          <g key={i} opacity={0.9 - i * 0.025}>
            <line x1="60" x2="540" y1={y + 16} y2={y + 16} stroke="#ffffff" strokeOpacity="0.07" />
            <rect x="60" y={y} width={w} height="8" rx="4" fill="#ffffff" fillOpacity="0.1" />
            <rect x={470 - ((i * 31) % 40)} y={y} width={70 + ((i * 31) % 40)} height="8" rx="4" fill={i % 5 === 2 ? '#e6b23c' : '#ffffff'} fillOpacity={i % 5 === 2 ? 0.85 : 0.16} />
          </g>
        );
      })}
      <rect width="600" height="800" fill="url(#fade)" />
    </svg>
  );
}
